import { useEffect, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { enqueueSnackbar } from "notistack";

import { api } from "../../../services/api";
import { PageTitle } from "../../../components/PageTitle";

interface Zone {
  id: string;
  name: string;
  color?: string;
  status: string;
}

interface Costumer {
  id: string;
  name: string;
  zone_id: string | null;
  zone?: {
    id: string;
    name: string;
  };
}

export function ZoneAssignCostumers() {
  const [zones, setZones] = useState<Zone[]>([]);
  const [costumers, setCostumers] = useState<Costumer[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<string>("");
  const [filterZoneId, setFilterZoneId] = useState<string>("");
  const [selectedCostumerIds, setSelectedCostumerIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Carregar zonas e clientes
  useEffect(() => {
    const loadData = async () => {
      try {
        const [zonesResponse, costumersResponse] = await Promise.all([
          api.get("/zones"),
          api.get("/costumers", { params: { status: "A" } }),
        ]);

        setZones(zonesResponse.data.filter((z: Zone) => z.status === "A"));
        setCostumers(costumersResponse.data);
      } catch {
        // Error handling
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filtrar clientes disponíveis (que não estão selecionados)
  const costumersDisponiveis = costumers
    .filter(c => {
      // Se tem filtro de zona, filtra por ela
      if (filterZoneId === "null") {
        return c.zone_id === null && !selectedCostumerIds.includes(c.id);
      }
      if (filterZoneId) {
        return (
          c.zone_id === filterZoneId && !selectedCostumerIds.includes(c.id)
        );
      }
      // Se não tem filtro, mostra todos não selecionados
      return !selectedCostumerIds.includes(c.id);
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  // Clientes selecionados para atribuir à zona
  const costumersSelecionados = costumers
    .filter(c => selectedCostumerIds.includes(c.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Adicionar cliente à seleção
  const adicionarCliente = (id: string) => {
    setSelectedCostumerIds(prev => [...prev, id]);
  };

  // Remover cliente da seleção
  const removerCliente = (id: string) => {
    setSelectedCostumerIds(prev => prev.filter(cid => cid !== id));
  };

  // Salvar atribuições
  const handleSave = async () => {
    if (!selectedZoneId) {
      enqueueSnackbar("Selecione uma zona de destino!", {
        variant: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }

    if (selectedCostumerIds.length === 0) {
      enqueueSnackbar("Selecione pelo menos um cliente!", {
        variant: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }

    setSaving(true);

    try {
      await api.post("/costumers/assign-zone", {
        zone_id: selectedZoneId,
        costumer_ids: selectedCostumerIds,
      });

      enqueueSnackbar(
        `${selectedCostumerIds.length} cliente(s) atribuído(s) à zona com sucesso!`,
        {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        },
      );

      // Atualizar lista de clientes
      const response = await api.get("/costumers");
      setCostumers(response.data);
      setSelectedCostumerIds([]);
    } catch (error) {
      enqueueSnackbar("Erro ao atribuir clientes à zona!", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    } finally {
      setSaving(false);
    }
  };

  // Obter nome da zona pelo ID
  const getZoneName = (zoneId: string | null) => {
    if (!zoneId) return "Sem zona";
    const zone = zones.find(z => z.id === zoneId);
    return zone?.name || "Sem zona";
  };

  // Obter cor da zona pelo ID
  const getZoneColor = (zoneId: string | null) => {
    if (!zoneId) return "#000000";
    const zone = zones.find(z => z.id === zoneId);
    return zone?.color || "#0085BD";
  };

  if (loading) {
    return (
      <div className="container mx-auto">
        <PageTitle
          title="Atribuir Clientes à Zona"
          backButton
          backPath="/zones"
        />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0085BD]" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      <PageTitle
        title="Atribuir Clientes à Zona"
        backButton
        backPath="/zones"
      />

      {/* Seleção da zona de destino */}
      <div className="bg-white p-4 rounded-md mb-6">
        <div className="space-y-2">
          <span className="text-sm font-medium text-[#0085BD]">
            Zona de Destino
          </span>
          <select
            value={selectedZoneId}
            onChange={e => setSelectedZoneId(e.target.value)}
            className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
          >
            <option value="">Selecione a zona de destino...</option>
            {zones.map(zone => (
              <option key={zone.id} value={zone.id}>
                {zone.name}
              </option>
            ))}
          </select>
          {selectedZoneId && (
            <div className="flex items-center gap-2 mt-2">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: getZoneColor(selectedZoneId) }}
              />
              <span className="text-sm text-[#7FA5B2]">
                Os clientes selecionados serão atribuídos a esta zona
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Filtro de zona para listar clientes */}
      <div className="bg-white p-4 rounded-md mb-6">
        <div className="space-y-2">
          <span className="text-sm font-medium text-[#0085BD]">
            Filtrar Clientes por Zona
          </span>
          <select
            value={filterZoneId}
            onChange={e => setFilterZoneId(e.target.value)}
            className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
          >
            <option value="">Todas as zonas</option>
            <option value="null">Sem zona</option>
            {zones.map(zone => (
              <option key={zone.id} value={zone.id}>
                {zone.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Layout de duas colunas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Clientes Disponíveis */}
        <div className="bg-white rounded-lg overflow-hidden">
          <div className="bg-[#0085BD] text-white px-4 py-3 font-medium flex items-center justify-between">
            <span>Clientes Disponíveis</span>
            <span className="bg-white/20 px-2 py-1 rounded text-sm">
              {costumersDisponiveis.length}
            </span>
          </div>
          <div className="h-96 overflow-y-auto p-2">
            {costumersDisponiveis.length === 0 ? (
              <div className="flex items-center justify-center h-full text-[#7FA5B2]">
                Nenhum cliente disponível
              </div>
            ) : (
              costumersDisponiveis.map(costumer => (
                <button
                  key={costumer.id}
                  type="button"
                  onClick={() => adicionarCliente(costumer.id)}
                  className="flex items-center justify-between p-3 hover:bg-[#E5F3F7] rounded-lg cursor-pointer transition-colors group w-full text-left"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{
                        backgroundColor: getZoneColor(costumer.zone_id),
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {costumer.name}
                      </div>
                      <div className="text-xs text-[#7FA5B2]">
                        {getZoneName(costumer.zone_id)}
                      </div>
                    </div>
                  </div>
                  <FiChevronRight
                    size={20}
                    className="text-[#0085BD] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  />
                </button>
              ))
            )}
          </div>
        </div>

        {/* Clientes Selecionados */}
        <div className="bg-white rounded-lg overflow-hidden">
          <div className="bg-[#92C163] text-white px-4 py-3 font-medium flex items-center justify-between">
            <span>Clientes Selecionados</span>
            <span className="bg-white/20 px-2 py-1 rounded text-sm">
              {costumersSelecionados.length}
            </span>
          </div>
          <div className="h-96 overflow-y-auto p-2">
            {costumersSelecionados.length === 0 ? (
              <div className="flex items-center justify-center h-full text-[#7FA5B2]">
                Nenhum cliente selecionado
              </div>
            ) : (
              costumersSelecionados.map(costumer => (
                <button
                  key={costumer.id}
                  type="button"
                  onClick={() => removerCliente(costumer.id)}
                  className="flex items-center justify-between p-3 hover:bg-[#f0f9e8] rounded-lg cursor-pointer transition-colors group w-full text-left"
                >
                  <FiChevronLeft
                    size={20}
                    className="text-[#92C163] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  />
                  <div className="flex items-center gap-2 flex-1 min-w-0 ml-2">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{
                        backgroundColor: getZoneColor(costumer.zone_id),
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {costumer.name}
                      </div>
                      <div className="text-xs text-[#7FA5B2]">
                        {getZoneName(costumer.zone_id)}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Botão de salvar */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={
            saving || !selectedZoneId || selectedCostumerIds.length === 0
          }
          className="bg-[#92C163] text-white px-6 py-3 rounded-lg hover:bg-[#7da854] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              Salvando...
            </>
          ) : (
            <>Atribuir {selectedCostumerIds.length} Cliente(s) à Zona</>
          )}
        </button>
      </div>
    </div>
  );
}
